"use client"

import { Phone } from "lucide-react"
import { WhatsAppIcon } from "@/components/icon"
import { CONTACT } from "@/lib/constants"
import { motion } from "framer-motion"

export function FloatingActionButtonsSimple() {
  const buttons = [
    {
      icon: Phone,
      label: "Call Now",
      sublabel: CONTACT.phone.display,
      href: `tel:${CONTACT.phone.primary}`,
      bgColor: "bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
      ariaLabel: "Call us now",
      ringColor: "ring-blue-400",
    },
    {
      icon: WhatsAppIcon,
      label: "WhatsApp",
      sublabel: "Chat with us",
      href: `https://wa.me/${CONTACT.phone.whatsapp}?text=Hi, I would like to get a quote for relocation services.`,
      bgColor: "bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700",
      isExternal: true,
      ariaLabel: "Chat on WhatsApp",
      ringColor: "ring-green-400",
    },
  ]

  return (
    <>
      {/* Mobile: Bottom Fixed Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="grid grid-cols-2 gap-0">
          {buttons.map((button, index) => (
            <a
              key={index}
              href={button.href}
              target={button.isExternal ? "_blank" : undefined}
              rel={button.isExternal ? "noopener noreferrer" : undefined}
              aria-label={button.ariaLabel}
              className={`${button.bgColor} text-white py-4 px-6 flex items-center justify-center gap-3 transition-all duration-300 active:scale-95`}
            >
              <button.icon size={22} />
              <div className="text-left">
                <p className="font-bold text-sm leading-tight">{button.label}</p>
                <p className="text-xs opacity-90">{button.sublabel}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Desktop: Floating Buttons */}
      <div className="hidden md:flex fixed bottom-6 left-6 z-[9998] flex-col gap-4">
        {buttons.map((button, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ 
              delay: index * 0.15 + 0.3, 
              type: "spring",
              stiffness: 200,
              damping: 15
            }}
            className="relative group"
          >
            <motion.a
              href={button.href}
              target={button.isExternal ? "_blank" : undefined}
              rel={button.isExternal ? "noopener noreferrer" : undefined}
              aria-label={button.ariaLabel}
              className={`${button.bgColor} text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 w-14 h-14 flex items-center justify-center relative`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <button.icon size={24} className="relative z-10 flex-shrink-0" />
              
              {/* Pulse Animation */}
              <motion.div
                className={`absolute inset-0 rounded-full ${button.bgColor} opacity-75`}
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.a>

            {/* Tooltip on Hover */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              whileHover={{ opacity: 1, x: 0 }}
              className="absolute left-full ml-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
              <div className={`${button.bgColor} text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap`}>
                <p className="font-bold text-sm leading-tight">{button.label}</p>
                <p className="text-xs opacity-90 leading-tight">{button.sublabel}</p>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </>
  )
}
